/**
 * #4619 Slice 4 — THE central bindingRef resolver (Codex APPROVE 5309690309,
 * forward requirement i).
 *
 * A compilation artifact's `AnswerContextFact.bindingRef` is the opaque,
 * exact, per-turn identity of ONE governed binding: the entry's position in
 * the deterministic compilation-pool walk (`pool:<index>`). This module owns
 * BOTH sides of that identity:
 *
 *   - `walkAnswerContextBindingPool` — THE canonical pool walk. The compiler's
 *     own pool construction imports and reuses this walk, so a ref minted by
 *     `compileAnswerContext` and a ref resolved here can never disagree about
 *     which binding an index names.
 *   - `resolveAnswerContextBindingRef` — THE ONLY resolution path any consumer
 *     uses. It returns the EXACT original binding object of the supplied pool
 *     (reference-identical, therefore byte-identical when serialized — forward
 *     requirement ii), never a reconstruction. Consumers never re-walk the
 *     pool themselves and never `.find()` by placeholder: two bindings sharing
 *     a placeholder but differing in scope, period, value, or evidence remain
 *     distinguishable only through this positional identity.
 *
 * FAIL-CLOSED LAW (forward requirement iii). A ref that cannot resolve
 * uniquely against the exact compilation pool resolves to a typed
 * `failed_closed` result, never to a guess:
 *
 *   - `malformed_binding_ref`    — not the exact `pool:<non-negative int>`
 *                                  grammar (no signs, no leading zeros, no
 *                                  whitespace, no float forms);
 *   - `binding_ref_outside_pool` — parses, but names no entry of THIS pool
 *                                  (the ref was minted against a different or
 *                                  larger pool);
 *   - `binding_identity_mismatch`— the entry at that position does not carry
 *                                  the caller's expected placeholder/moduleId
 *                                  identity. This is the wrong-pool detector:
 *                                  a structurally different pool (reordered,
 *                                  filtered, or from another turn) fails
 *                                  closed instead of silently returning a
 *                                  different governed number.
 *
 * Position within a single pool is unique by construction (one entry per
 * index), so "resolve uniquely" reduces to: the ref parses, the position
 * exists, and the identity check passes.
 */

/** The minimal structural slice the pool walk reads off a binding. */
export type AnswerContextPoolBindingLike = {
  readonly placeholder?: string | null;
};

/** One module's contribution to a binding pool, generically typed so the
 * resolver hands back the caller's OWN binding type (e.g. `NumericBinding`). */
export type AnswerContextBindingPoolModuleLike<TBinding extends AnswerContextPoolBindingLike> = {
  readonly moduleId?: string | null;
  readonly numericBindings?: readonly TBinding[] | null;
};

/** One canonical pool entry: the exact source binding plus its walk identity. */
export type AnswerContextBindingPoolEntry<TBinding extends AnswerContextPoolBindingLike> = {
  readonly index: number;
  /** The EXACT original binding object — never a copy. */
  readonly binding: TBinding;
  readonly placeholder: string;
  readonly moduleId: string | null;
};

function safeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** The one binding-ref grammar. Exported as the single mint point. */
export function answerContextBindingRefForPoolIndex(index: number): string {
  return `pool:${index}`;
}

const ANSWER_CONTEXT_BINDING_REF_PATTERN = /^pool:(0|[1-9][0-9]*)$/;

/**
 * THE canonical deterministic pool walk: modules in order, bindings in order,
 * skipping only bindings with a blank placeholder. `compileAnswerContext`
 * builds its pool from these entries, so `pool:<index>` refs are defined
 * against exactly this sequence.
 */
export function walkAnswerContextBindingPool<TBinding extends AnswerContextPoolBindingLike>(
  moduleResults: readonly AnswerContextBindingPoolModuleLike<TBinding>[] | null | undefined
): AnswerContextBindingPoolEntry<TBinding>[] {
  const entries: AnswerContextBindingPoolEntry<TBinding>[] = [];
  for (const moduleResult of moduleResults ?? []) {
    const moduleId = safeString(moduleResult?.moduleId).trim() || null;
    for (const binding of moduleResult?.numericBindings ?? []) {
      const placeholder = safeString(binding?.placeholder).trim();
      if (!placeholder) continue;
      entries.push({ index: entries.length, binding, placeholder, moduleId });
    }
  }
  return entries;
}

export const ANSWER_CONTEXT_BINDING_REF_FAILURE_REASONS = Object.freeze([
  'malformed_binding_ref',
  'binding_ref_outside_pool',
  'binding_identity_mismatch',
] as const);

export type AnswerContextBindingRefFailureReason =
  (typeof ANSWER_CONTEXT_BINDING_REF_FAILURE_REASONS)[number];

export type AnswerContextBindingRefResolution<TBinding extends AnswerContextPoolBindingLike> =
  | {
      readonly status: 'resolved';
      /** The exact original pool binding object — reference-identical. */
      readonly binding: TBinding;
      readonly placeholder: string;
      readonly moduleId: string | null;
      readonly index: number;
    }
  | {
      readonly status: 'failed_closed';
      readonly reason: AnswerContextBindingRefFailureReason;
    };

/**
 * Expected identity of the binding a ref should resolve to, carried off the
 * compilation fact (`placeholder`, `moduleId`). Supplying it arms the
 * wrong-pool detector; a compilation fact always carries both, so consumers
 * always supply both.
 */
export type AnswerContextBindingRefExpectedIdentity = {
  readonly placeholder?: string | null;
  /** `undefined` skips the check; `null` means "expected no module id". */
  readonly moduleId?: string | null;
};

/**
 * Resolve `bindingRef` against `moduleResults` — the SAME pool input the
 * compilation ran over — to the exact original binding object, or fail
 * closed with a typed reason. See the module docstring for the law.
 */
export function resolveAnswerContextBindingRef<TBinding extends AnswerContextPoolBindingLike>(
  moduleResults: readonly AnswerContextBindingPoolModuleLike<TBinding>[] | null | undefined,
  bindingRef: string,
  expected?: AnswerContextBindingRefExpectedIdentity
): AnswerContextBindingRefResolution<TBinding> {
  const match = ANSWER_CONTEXT_BINDING_REF_PATTERN.exec(
    typeof bindingRef === 'string' ? bindingRef : ''
  );
  if (!match) return { status: 'failed_closed', reason: 'malformed_binding_ref' };
  const index = Number(match[1]);
  if (!Number.isSafeInteger(index)) {
    return { status: 'failed_closed', reason: 'malformed_binding_ref' };
  }
  const entries = walkAnswerContextBindingPool(moduleResults);
  const entry = index < entries.length ? entries[index] : null;
  if (!entry) {
    return { status: 'failed_closed', reason: 'binding_ref_outside_pool' };
  }
  if (expected) {
    const expectedPlaceholder = safeString(expected.placeholder).trim();
    if (expectedPlaceholder && entry.placeholder !== expectedPlaceholder) {
      return { status: 'failed_closed', reason: 'binding_identity_mismatch' };
    }
    if (expected.moduleId !== undefined) {
      const expectedModuleId = safeString(expected.moduleId).trim() || null;
      if (entry.moduleId !== expectedModuleId) {
        return { status: 'failed_closed', reason: 'binding_identity_mismatch' };
      }
    }
  }
  return {
    status: 'resolved',
    binding: entry.binding,
    placeholder: entry.placeholder,
    moduleId: entry.moduleId,
    index: entry.index,
  };
}
